/**
 * Contact form state — single-step form fields, step (form | success), errors, options, open detail.
 */
import { computed, ref } from 'vue'
import type {
  ContactMeOpenDetail,
  ContactMeStep,
  ContactMeWidgetOptions,
  ContactMeFormModel,
} from '../types'

export type FormState = {
  firstName: string
  email: string
  phone: string
  hasWhatsApp: boolean
  budget: string
  timeline: string
}

const initialForm: FormState = {
  firstName: '',
  email: '',
  phone: '',
  hasWhatsApp: true,
  budget: '',
  timeline: '',
}

export function useContactFormState(opts: { t: (key: string) => string }) {
  const { t } = opts
  const optsRef = ref<ContactMeWidgetOptions>({})
  const openDetail = ref<ContactMeOpenDetail | undefined>(undefined)
  const isOpen = ref(false)
  const step = ref<ContactMeStep>(1)
  const isBusy = ref(false)
  const isShaking = ref(false)
  const errors = ref<Partial<Record<keyof FormState, string>>>({})
  const form = ref<FormState>({ ...initialForm })

  const formModel = computed<ContactMeFormModel>(() => ({
    firstName: form.value.firstName,
    email: form.value.email,
    phone: form.value.phone,
    hasWhatsApp: form.value.hasWhatsApp,
    budget: form.value.budget,
    timeline: form.value.timeline,
  }))

  const brandTitle = computed(() => optsRef.value.brandName ?? t('contactForm.headerTitle'))
  const headerTitle = computed(() =>
    step.value === 2 ? t('contactForm.headerTitleSuccess') : t('contactForm.headerTitleExpert')
  )
  const headerSubtitle = computed(() =>
    step.value === 2 ? t('contactForm.headerSubtitleSuccess') : t('contactForm.headerSubtitleExpert')
  )

  function setError(key: keyof FormState, msg: string) {
    errors.value = { ...errors.value, [key]: msg }
  }

  function clearError(key: keyof FormState) {
    if (!errors.value[key]) return
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- rest omit
    const { [key]: _omit, ...rest } = errors.value
    errors.value = rest
  }

  function updateForm(partial: Partial<FormState>) {
    form.value = { ...form.value, ...partial }
    Object.keys(partial).forEach(k => clearError(k as keyof FormState))
  }

  function reset() {
    openDetail.value = undefined
    isBusy.value = false
    step.value = 1
    errors.value = {}
    form.value = { ...initialForm, hasWhatsApp: true }
  }

  function applyPrefill(prefill?: ContactMeOpenDetail['prefill']) {
    if (!prefill) return
    if (typeof prefill.email === 'string') form.value.email = prefill.email
    if (typeof prefill.firstName === 'string') form.value.firstName = prefill.firstName
    if (typeof prefill.phone === 'string') form.value.phone = prefill.phone
    if (typeof prefill.hasWhatsApp === 'boolean') form.value.hasWhatsApp = prefill.hasWhatsApp
    if (typeof prefill.budget === 'string') form.value.budget = prefill.budget
    if (typeof prefill.timeline === 'string') form.value.timeline = prefill.timeline
  }

  function shake() {
    isShaking.value = false
    void document.body.offsetHeight
    isShaking.value = true
    window.setTimeout(() => (isShaking.value = false), 650)
  }

  return {
    optsRef,
    openDetail,
    isOpen,
    step,
    isBusy,
    isShaking,
    errors,
    form,
    formModel,
    brandTitle,
    headerTitle,
    headerSubtitle,
    setError,
    clearError,
    updateForm,
    reset,
    applyPrefill,
    shake,
  }
}
